import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, setAdminToken } from '../lib/adminApi';

export const AdminLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      setAdminToken(data.access_token);
      toast.success(`Welcome, ${data.username}!`);
      navigate('/admin');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Login failed';
      toast.error(typeof detail === 'string' ? detail : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen pt-32 pb-24 flex items-start justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md px-6"
      >
        <div className="border border-primary/20 p-10 space-y-8 bg-background/50 backdrop-blur-sm">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-primary/30 mb-2">
              <Lock size={24} className="text-primary" strokeWidth={1.5} />
            </div>
            <h1 className="font-display text-4xl text-foreground">
              Admin <span className="italic">Login</span>
            </h1>
            <p className="text-sm text-muted-foreground uppercase tracking-widest">
              Krishna Sugandh Management
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-widest font-medium block mb-3 text-muted-foreground">
                Username
              </label>
              <div className="flex items-center border-b border-white/20 focus-within:border-primary transition-colors">
                <User size={18} className="text-muted-foreground mr-3" strokeWidth={1.5} />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  data-testid="admin-login-username"
                  className="flex-1 bg-transparent py-3 focus:outline-none text-lg"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest font-medium block mb-3 text-muted-foreground">
                Password
              </label>
              <div className="flex items-center border-b border-white/20 focus-within:border-primary transition-colors">
                <Lock size={18} className="text-muted-foreground mr-3" strokeWidth={1.5} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-login-password"
                  className="flex-1 bg-transparent py-3 focus:outline-none text-lg"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 uppercase tracking-widest text-sm font-medium transition-all duration-300 disabled:opacity-50"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};
